import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Route,
  FileSearch,
  ClipboardList,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  ArrowLeftRight,
  ArrowLeft,
  ArrowRightLeft,
  List,
  BookOpen,
  Scan,
  XCircle,
  Monitor,
  FileText,
  PackageSearch,
  Truck,
  UtensilsCrossed,
} from 'lucide-react';

/* ─── FAQs ───────────────────────────────────────────── */

const faqs = [
  {
    q: '¿Es obligatoria la trazabilidad alimentaria en restaurantes?',
    a: 'Si. El Reglamento (CE) 178/2002 obliga a todos los operadores de empresas alimentarias de la Union Europea — incluidos restaurantes, bares, catering y comedores — a mantener un sistema de trazabilidad que permita identificar al proveedor de cada producto y el destino de los alimentos que sirven.',
  },
  {
    q: '¿Que informacion minima debo registrar para la trazabilidad?',
    a: 'Como minimo debes registrar: nombre del producto, proveedor (nombre y datos de contacto), numero de lote, fecha de recepcion, fecha de caducidad o consumo preferente, cantidad recibida y condiciones de almacenamiento. Si vendes a otros operadores (catering), tambien debes registrar a quien has suministrado cada producto.',
  },
  {
    q: '¿Cuanto tiempo debo conservar los registros de trazabilidad?',
    a: 'Segun la normativa espanola, los registros de trazabilidad deben conservarse durante un minimo de 5 anos. Algunos inspectores recomiendan mantenerlos hasta 6 anos para cubrir posibles reclamaciones. Con un sistema digital como PLEKO, el almacenamiento es automatico e ilimitado.',
  },
  {
    q: '¿Que diferencia hay entre trazabilidad y APPCC?',
    a: 'La trazabilidad permite rastrear un alimento a lo largo de la cadena alimentaria (de donde viene y adonde va). El APPCC (Analisis de Peligros y Puntos de Control Critico) identifica y controla los riesgos sanitarios durante la manipulacion. Son sistemas complementarios: la trazabilidad dice "de donde viene esto" y el APPCC dice "como lo manipulamos de forma segura".',
  },
  {
    q: '¿Puedo llevar la trazabilidad en papel?',
    a: 'Si, la normativa no exige un formato digital. Sin embargo, el papel es propenso a errores, dificil de buscar en caso de alerta alimentaria y ocupa espacio fisico. Un sistema digital permite localizar cualquier producto en segundos, algo critico cuando Sanidad activa una alerta y tienes minutos para identificar si te afecta.',
  },
  {
    q: '¿Que pasa si hay una alerta alimentaria y no tengo trazabilidad?',
    a: 'Sin trazabilidad no puedes demostrar rapidamente si el producto afectado esta en tu cocina, si lo has servido o a quien. Esto puede convertir una simple retirada en una crisis sanitaria para tu restaurante, con sanciones economicas, cierre temporal y dano reputacional grave.',
  },
  {
    q: '¿La trazabilidad incluye tambien los platos que sirvo al cliente?',
    a: 'Si. La trazabilidad interna debe permitirte vincular cada plato servido con los lotes de ingredientes utilizados ese dia. No necesitas registrar cada plato individual, pero si debes poder reconstruir que lotes de materia prima se usaron en cada servicio.',
  },
  {
    q: '¿Como ayuda PLEKO con la trazabilidad alimentaria?',
    a: 'Cuando escaneas una factura con el OCR de PLEKO, el sistema registra automaticamente el proveedor, los productos, los precios y la fecha de recepcion. Al vincular ingredientes con recetas, puedes rastrear desde el plato servido hasta el proveedor original. Ademas, PLEKO almacena todo el historico de compras de forma digital y buscable.',
  },
];

/* ─── Table of Contents ──────────────────────────────── */

const toc = [
  { id: 'que-es', label: 'Que es la trazabilidad alimentaria' },
  { id: 'por-que-obligatoria', label: 'Por que es obligatoria en restaurantes' },
  { id: 'tipos', label: 'Los 3 tipos de trazabilidad' },
  { id: 'que-registrar', label: 'Que informacion debes registrar' },
  { id: 'implementar', label: 'Como implementar la trazabilidad paso a paso' },
  { id: 'tabla-registro', label: 'Tabla: registro tipo de trazabilidad' },
  { id: 'alergenos', label: 'Trazabilidad y alergenos' },
  { id: 'appcc', label: 'Trazabilidad y APPCC' },
  { id: 'sanciones', label: 'Sanciones por falta de trazabilidad' },
  { id: 'digital-vs-papel', label: 'Trazabilidad digital vs papel' },
  { id: 'pleko', label: 'Como PLEKO facilita la trazabilidad' },
  { id: 'errores', label: 'Errores comunes en trazabilidad' },
  { id: 'faqs', label: 'Preguntas frecuentes' },
];

/* ─── Schema.org JSON-LD ─────────────────────────────── */

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Trazabilidad Alimentaria en Restaurantes',
      item: 'https://pleko.app/trazabilidad-alimentaria',
    },
  ],
};

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Trazabilidad Alimentaria en Restaurantes: Que Es, Normativa y Como Implementarla',
  description:
    'Guia completa sobre trazabilidad alimentaria en restaurantes. Normativa CE 178/2002, tipos de trazabilidad, registros obligatorios, sanciones y como digitalizarla.',
  author: { '@type': 'Organization', name: 'PLEKO' },
  publisher: { '@type': 'Organization', name: 'PLEKO', url: 'https://pleko.app' },
  datePublished: '2026-06-18',
  dateModified: '2026-06-18',
  mainEntityOfPage: 'https://pleko.app/trazabilidad-alimentaria',
};

/* ─── Component ──────────────────────────────────────── */

export default function TrazabilidadAlimentariaPage() {
  return (
    <>
      <Helmet>
        <title>Trazabilidad Alimentaria en Restaurantes: Normativa 2026 y Como Implementarla | PLEKO</title>
        <meta
          name="description"
          content="Guia completa sobre trazabilidad alimentaria en restaurantes. Normativa CE 178/2002, los 3 tipos de trazabilidad, registros obligatorios, sanciones por incumplimiento y como digitalizarla con PLEKO."
        />
        <meta
          name="keywords"
          content="trazabilidad alimentaria restaurante, trazabilidad alimentos, normativa trazabilidad restaurante, registro trazabilidad hosteleria, trazabilidad obligatoria, trazabilidad alimentaria hosteleria, Reglamento CE 178/2002, trazabilidad cocina"
        />
        <link rel="canonical" href="https://pleko.app/trazabilidad-alimentaria" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/trazabilidad-alimentaria" />
        <meta
          property="og:title"
          content="Trazabilidad Alimentaria en Restaurantes: Normativa 2026 y Como Implementarla"
        />
        <meta
          property="og:description"
          content="Guia completa de trazabilidad alimentaria para restaurantes. Normativa, tipos, registros obligatorios, sanciones y como digitalizarla."
        />
        <meta property="og:image" content="https://pleko.app/og-image.png" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Trazabilidad Alimentaria en Restaurantes: Normativa 2026 | PLEKO"
        />
        <meta
          name="twitter:description"
          content="Normativa CE 178/2002, tipos de trazabilidad, registros obligatorios, sanciones y como digitalizar la trazabilidad alimentaria."
        />
        <meta name="twitter:image" content="https://pleko.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* ═══════════ HERO ═══════════ */}
        <section className="bg-gradient-to-b from-teal-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium mb-6">
                <Route className="w-4 h-4" />
                Normativa de trazabilidad para hosteleria — 2026
              </span>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
                Trazabilidad Alimentaria en Restaurantes:{' '}
                <span className="text-teal-600">Que Es, Normativa y Como Implementarla</span>
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                Multas de hasta 600.000 euros por incumplimiento. Conoce la normativa, los 3 tipos de trazabilidad,
                que debes registrar y como digitalizar el proceso para cumplir sin esfuerzo.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#que-es"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700 transition-colors shadow-lg shadow-teal-200"
                >
                  Leer la guia completa
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-slate-200 text-slate-700 font-semibold rounded-xl hover:border-slate-300 transition-colors"
                >
                  <Scan className="w-4 h-4" />
                  Probar PLEKO gratis
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ CONTENT ═══════════ */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Table of Contents */}
          <nav className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-5 h-5 text-teal-600" />
              <h2 className="text-lg font-bold text-slate-900">Contenido de esta guia</h2>
            </div>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {toc.map((item, i) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-sm text-teal-600 hover:text-teal-800 hover:underline">
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="prose prose-lg max-w-none">
            {/* ═══════════ SECTION 1: QUE ES ═══════════ */}
            <h2 id="que-es" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Que es la trazabilidad alimentaria
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La trazabilidad alimentaria es la capacidad de <strong>rastrear un alimento a lo largo de toda la cadena de produccion, transformacion y distribucion</strong> — desde su origen (granja, mar, fabrica) hasta que llega al consumidor final en tu restaurante.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              En la practica, significa poder responder en cualquier momento a estas preguntas: ¿de donde viene este ingrediente? ¿Quien me lo vendio? ¿Cuando lo recibi? ¿Que lote es? ¿En que platos lo he utilizado? Si no puedes responder en minutos, tu trazabilidad tiene lagunas.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              La trazabilidad no es solo un requisito legal — es una <strong>herramienta de proteccion</strong> para tu negocio. Cuando Sanidad activa una alerta alimentaria (por ejemplo, por contaminacion de un lote de huevos), un restaurante con buena trazabilidad puede verificar en minutos si tiene ese lote y retirarlo. Sin trazabilidad, puede tardar horas o dias, con el riesgo de servir producto contaminado.
            </p>

            {/* ═══════════ SECTION 2: POR QUE OBLIGATORIA ═══════════ */}
            <h2 id="por-que-obligatoria" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Por que la trazabilidad es obligatoria en restaurantes
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El <strong>Reglamento (CE) 178/2002</strong> del Parlamento Europeo establecio la trazabilidad como obligacion legal para todos los operadores de empresas alimentarias de la Union Europea. Su articulo 18 exige que cualquier operador — incluidos restaurantes, bares, catering y comedores — pueda identificar a sus proveedores y los productos que estos le han suministrado.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Este reglamento se complementa con el <strong>Reglamento (CE) 852/2004</strong> sobre higiene de productos alimenticios, que refuerza la obligacion de mantener registros adecuados. En Espana, la <strong>Ley 17/2011 de seguridad alimentaria y nutricion</strong> establece el regimen sancionador con multas que pueden alcanzar los 600.000 euros para infracciones muy graves.
            </p>
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 mb-10">
              <div className="flex gap-3">
                <ShieldCheck className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-bold text-teal-800 mb-2">Marco legal de la trazabilidad</p>
                  <ul className="space-y-1 text-sm text-teal-700">
                    <li><strong>Reglamento (CE) 178/2002, art. 18:</strong> Obligacion general de trazabilidad para toda la cadena alimentaria</li>
                    <li><strong>Reglamento (CE) 852/2004:</strong> Normas de higiene que refuerzan los requisitos de registro</li>
                    <li><strong>Ley 17/2011:</strong> Regimen sancionador en Espana — multas de 100 euros a 600.000 euros</li>
                    <li><strong>Real Decreto 1801/2003:</strong> Seguridad general de los productos</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* ═══════════ SECTION 3: TIPOS ═══════════ */}
            <h2 id="tipos" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Los 3 tipos de trazabilidad alimentaria
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La trazabilidad completa se compone de tres eslabones. Un restaurante debe implementar los tres para cumplir con la normativa:
            </p>
            <div className="space-y-4 mb-10">
              <div className="flex gap-4 p-5 bg-teal-50 rounded-xl border border-teal-200">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center">
                    <ArrowLeft className="w-5 h-5" />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 mb-1">1. Trazabilidad hacia atras (proveedores)</h3>
                  <p className="text-slate-600 text-sm mb-2">
                    Permite identificar el origen de cada materia prima: <strong>quien te vendio que producto, cuando, en que cantidad y con que lote</strong>. Es el eslabon mas critico para un restaurante.
                  </p>
                  <p className="text-slate-500 text-xs">Ejemplo: "Las gambas del plato de hoy vienen del proveedor Mariscos Garcia, lote MG-2026-0412, recibidas el 15/06/2026."</p>
                </div>
              </div>
              <div className="flex gap-4 p-5 bg-cyan-50 rounded-xl border border-cyan-200">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-cyan-600 text-white rounded-full flex items-center justify-center">
                    <ArrowRightLeft className="w-5 h-5" />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 mb-1">2. Trazabilidad interna (procesos)</h3>
                  <p className="text-slate-600 text-sm mb-2">
                    Documenta <strong>que ocurre con el producto dentro de tu cocina</strong>: como se almacena, se transforma y se combina con otros ingredientes para elaborar los platos.
                  </p>
                  <p className="text-slate-500 text-xs">Ejemplo: "Las gambas del lote MG-2026-0412 se usaron en la receta de arroz con mariscos servido el 16/06/2026."</p>
                </div>
              </div>
              <div className="flex gap-4 p-5 bg-sky-50 rounded-xl border border-sky-200">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-sky-600 text-white rounded-full flex items-center justify-center">
                    <ArrowLeftRight className="w-5 h-5" />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 mb-1">3. Trazabilidad hacia adelante (cliente/destino)</h3>
                  <p className="text-slate-600 text-sm mb-2">
                    Registra <strong>a quien has suministrado los productos</strong>. En un restaurante, el consumidor final es el comensal. En catering o servicios de colectividades, debes registrar a quien entregas la comida.
                  </p>
                  <p className="text-slate-500 text-xs">Ejemplo: "El catering del 17/06/2026 para Empresa ABC incluia arroz con mariscos elaborado con lote MG-2026-0412."</p>
                </div>
              </div>
            </div>

            {/* ═══════════ SECTION 4: QUE REGISTRAR ═══════════ */}
            <h2 id="que-registrar" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Que informacion debes registrar
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Para cada producto que entra en tu cocina, la normativa exige registrar al menos los siguientes datos. Esta tabla resume los campos minimos que debe contener tu registro de trazabilidad:
            </p>
            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Campo</th>
                    <th className="p-3 text-left font-medium">Descripcion</th>
                    <th className="p-3 text-center font-medium">Obligatorio</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Nombre del producto', 'Denominacion comercial del alimento recibido', 'Si'],
                    ['Proveedor', 'Nombre, direccion y CIF de quien te lo vendio', 'Si'],
                    ['Numero de lote', 'Identificador del lote del fabricante/distribuidor', 'Si'],
                    ['Fecha de recepcion', 'Dia exacto en que recibiste el producto', 'Si'],
                    ['Fecha de caducidad', 'Consumo preferente o fecha de caducidad del producto', 'Si'],
                    ['Cantidad recibida', 'Kilos, litros o unidades recepcionadas', 'Si'],
                    ['Temperatura recepcion', 'Temperatura del producto al recibirlo (refrigerados/congelados)', 'Recomendado'],
                    ['Destino/uso', 'En que platos o preparaciones se ha utilizado', 'Recomendado'],
                  ].map(([campo, desc, obligatorio], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{campo}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{desc}</td>
                      <td className="p-3 border-b border-slate-100 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${obligatorio === 'Si' ? 'bg-teal-100 text-teal-700' : 'bg-amber-100 text-amber-700'}`}>
                          {obligatorio}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ═══════════ SECTION 5: IMPLEMENTAR ═══════════ */}
            <h2 id="implementar" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Como implementar la trazabilidad paso a paso
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Implementar un sistema de trazabilidad no tiene por que ser complicado. Sigue estos 6 pasos para montar un sistema funcional en tu restaurante:
            </p>
            <div className="space-y-4 mb-10">
              {[
                {
                  num: '1',
                  title: 'Crea un registro de proveedores',
                  desc: 'Documenta todos tus proveedores con nombre, CIF, direccion, telefono y productos que te suministran. Cada producto debe estar vinculado a al menos un proveedor.',
                  icon: Truck,
                },
                {
                  num: '2',
                  title: 'Establece un protocolo de recepcion',
                  desc: 'Define que datos se registran cuando llega una entrega: producto, proveedor, lote, cantidad, fecha de caducidad y temperatura (para refrigerados y congelados). Forma al equipo que recepciona.',
                  icon: ClipboardList,
                },
                {
                  num: '3',
                  title: 'Etiqueta todo lo que almacenas',
                  desc: 'Cada producto en camara, almacen o congelador debe tener una etiqueta con: nombre, lote, fecha de recepcion y fecha de caducidad. Aplica rotacion FIFO (primero en entrar, primero en salir).',
                  icon: FileText,
                },
                {
                  num: '4',
                  title: 'Vincula ingredientes con recetas',
                  desc: 'Documenta que ingredientes (y de que lotes) se usan en cada receta. Esto conecta la trazabilidad hacia atras (proveedor) con la interna (preparacion).',
                  icon: UtensilsCrossed,
                },
                {
                  num: '5',
                  title: 'Registra la produccion diaria',
                  desc: 'Anota que platos se han preparado cada dia y con que lotes de materia prima. No hace falta registrar cada plato individual, pero si poder reconstruir los lotes usados en cada servicio.',
                  icon: PackageSearch,
                },
                {
                  num: '6',
                  title: 'Digitaliza y automatiza',
                  desc: 'Sustituye las libretas y hojas de calculo por un sistema digital que registre automaticamente los datos de trazabilidad. El OCR de facturas de PLEKO captura proveedor, producto y fecha en cada escaneo.',
                  icon: Monitor,
                },
              ].map(({ num, title, desc, icon: Icon }) => (
                <div key={num} className="flex gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex-shrink-0">
                    <span className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-lg">{num}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 mb-1 flex items-center gap-2">
                      <Icon className="w-4 h-4 text-teal-500" />
                      {title}
                    </p>
                    <p className="text-slate-600 text-sm">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* ═══════════ SECTION 6: TABLA REGISTRO ═══════════ */}
            <h2 id="tabla-registro" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Tabla: ejemplo practico de registro de trazabilidad
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Asi deberia verse un registro de trazabilidad bien hecho. Este ejemplo muestra 5 productos recibidos en una semana tipica:
            </p>
            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Producto</th>
                    <th className="p-3 text-left font-medium">Proveedor</th>
                    <th className="p-3 text-center font-medium">Lote</th>
                    <th className="p-3 text-center font-medium">Recepcion</th>
                    <th className="p-3 text-center font-medium">Caducidad</th>
                    <th className="p-3 text-center font-medium">Cantidad</th>
                    <th className="p-3 text-left font-medium">Destino</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Solomillo de ternera', 'Carnicas Lopez', 'CL-4521', '10/06', '17/06', '8 kg', 'Solomillo al punto, Steak tartar'],
                    ['Gambas frescas', 'Mariscos Garcia', 'MG-0412', '10/06', '13/06', '5 kg', 'Arroz caldoso, Gambas al ajillo'],
                    ['Tomate pera', 'Frutas Baix Llobregat', 'FB-2206', '11/06', '18/06', '10 kg', 'Ensaladas, Gazpacho, Salsas'],
                    ['Aceite de oliva VE', 'Oleicola del Sur', 'OS-891', '09/06', '03/2027', '20 L', 'Cocina general'],
                    ['Huevos camperos', 'Avicola Penedes', 'AP-6633', '12/06', '10/07', '180 ud', 'Tortillas, Postres, Rebozados'],
                  ].map(([producto, proveedor, lote, recepcion, caducidad, cantidad, destino], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{producto}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{proveedor}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-mono text-xs text-teal-600">{lote}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600">{recepcion}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600">{caducidad}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600">{cantidad}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600 text-xs">{destino}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ═══════════ SECTION 7: ALERGENOS ═══════════ */}
            <h2 id="alergenos" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Trazabilidad y alergenos: una relacion directa
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La trazabilidad alimentaria y la <Link to="/alergenos-restaurante" className="text-teal-600 hover:underline">gestion de alergenos</Link> estan intima mente relacionadas. Si no puedes rastrear de donde viene cada ingrediente de un plato, tampoco puedes garantizar la informacion de alergenos que das al cliente.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Un cambio de proveedor puede significar un cambio en la composicion de un producto. Por ejemplo: tu proveedor habitual de pan rallado usa una formula sin lacteos, pero el proveedor alternativo si incluye leche. Sin trazabilidad, ese cambio pasa desapercibido y puedes servir un alergeno no declarado a un cliente alergico — con consecuencias potencialmente graves.
            </p>
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-teal-800">Conexion critica</p>
                  <p className="text-teal-700 text-sm mt-1">
                    Cada vez que cambies de proveedor o de producto, revisa la ficha de alergenos del nuevo producto y actualiza tus recetas. Un buen sistema de trazabilidad vincula proveedor → ingrediente → receta → alergenos de forma automatica.
                  </p>
                </div>
              </div>
            </div>

            {/* ═══════════ SECTION 8: APPCC ═══════════ */}
            <h2 id="appcc" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Trazabilidad y APPCC: sistemas complementarios
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El <strong>APPCC</strong> (Analisis de Peligros y Puntos de Control Critico) y la trazabilidad son dos pilares fundamentales de la seguridad alimentaria que se refuerzan mutuamente. El APPCC identifica los puntos criticos donde puede surgir un peligro (temperaturas, contaminacion cruzada, tiempos), mientras que la trazabilidad permite rastrear el origen y destino de cada alimento.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Cuando ocurre una incidencia — por ejemplo, una camara frigorifica que sube de temperatura durante la noche — la trazabilidad te permite identificar exactamente que productos estaban en esa camara, de que lotes son y si ya se han servido. Sin trazabilidad, tendrias que descartar todo por precaucion.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-10">
              <div className="p-5 bg-teal-50 border border-teal-100 rounded-xl">
                <h4 className="font-bold text-teal-800 mb-3 flex items-center gap-2">
                  <Route className="w-4 h-4" /> Trazabilidad
                </h4>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" /> ¿De donde viene este producto?</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" /> ¿Quien me lo vendio?</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" /> ¿Que lote es y cuando caduca?</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" /> ¿En que platos lo he usado?</li>
                </ul>
              </div>
              <div className="p-5 bg-sky-50 border border-sky-100 rounded-xl">
                <h4 className="font-bold text-sky-800 mb-3 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" /> APPCC
                </h4>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-sky-500 flex-shrink-0 mt-0.5" /> ¿Se ha almacenado a temperatura correcta?</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-sky-500 flex-shrink-0 mt-0.5" /> ¿Se ha manipulado con higiene?</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-sky-500 flex-shrink-0 mt-0.5" /> ¿Se ha cocinado a temperatura segura?</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-sky-500 flex-shrink-0 mt-0.5" /> ¿Se ha evitado contaminacion cruzada?</li>
                </ul>
              </div>
            </div>

            {/* ═══════════ SECTION 9: SANCIONES ═══════════ */}
            <h2 id="sanciones" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Sanciones por falta de trazabilidad
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La <strong>Ley 17/2011 de seguridad alimentaria y nutricion</strong> establece tres niveles de sancion por incumplimiento de las obligaciones de trazabilidad:
            </p>
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-10">
              <div className="flex gap-3">
                <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-bold text-red-800 mb-3">Regimen sancionador (Ley 17/2011)</p>
                  <div className="grid md:grid-cols-3 gap-3 text-sm">
                    <div className="bg-white rounded-lg p-3">
                      <p className="font-bold text-slate-800">Leve</p>
                      <p className="text-red-600 font-bold text-lg">100 - 5.000 euros</p>
                      <p className="text-slate-500 text-xs">Registros incompletos, datos parciales, etiquetado deficiente</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="font-bold text-slate-800">Grave</p>
                      <p className="text-red-600 font-bold text-lg">5.001 - 20.000 euros</p>
                      <p className="text-slate-500 text-xs">Ausencia de sistema de trazabilidad, negligencia, reincidencia</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="font-bold text-slate-800">Muy grave</p>
                      <p className="text-red-600 font-bold text-lg">20.001 - 600.000 euros</p>
                      <p className="text-slate-500 text-xs">Riesgo sanitario, ocultacion deliberada, obstruccion a la inspeccion</p>
                    </div>
                  </div>
                  <p className="text-red-700 text-xs mt-3">
                    Ademas de las multas, las infracciones graves pueden conllevar el cierre temporal del establecimiento y la publicacion de la sancion.
                  </p>
                </div>
              </div>
            </div>

            {/* ═══════════ SECTION 10: DIGITAL VS PAPEL ═══════════ */}
            <h2 id="digital-vs-papel" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Trazabilidad digital vs papel: ventajas del software
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Aunque la normativa permite llevar la trazabilidad en papel, la realidad es que un sistema manual no escala y falla cuando mas lo necesitas — durante una alerta alimentaria, cuando el inspector llama a la puerta o cuando necesitas localizar un lote urgentemente.
            </p>
            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Criterio</th>
                    <th className="p-3 text-center font-medium">Papel / Excel</th>
                    <th className="p-3 text-center font-medium">Software digital</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Velocidad de busqueda', 'Minutos u horas', 'Segundos'],
                    ['Errores humanos', 'Frecuentes', 'Minimos'],
                    ['Espacio fisico', 'Archivadores y carpetas', 'Cero (cloud)'],
                    ['Alertas automaticas', 'No disponible', 'Caducidades, temperaturas'],
                    ['Integracion con recetas', 'Manual', 'Automatica'],
                    ['Acceso remoto', 'No', 'Si, desde cualquier dispositivo'],
                    ['Coste inicial', 'Bajo (papel)', 'Medio (suscripcion)'],
                    ['Cumplimiento normativo', 'Dificil de demostrar', 'Facil con historico digital'],
                  ].map(([criterio, papel, digital], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{criterio}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-red-500">{papel}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-teal-600 font-medium">{digital}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ═══════════ SECTION 11: PLEKO ═══════════ */}
            <h2 id="pleko" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Como PLEKO facilita la trazabilidad alimentaria
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              PLEKO no es un software de trazabilidad dedicado, pero sus herramientas de gestion de costes cubren automaticamente gran parte de los requisitos de trazabilidad hacia atras:
            </p>
            <div className="space-y-4 mb-10">
              {[
                {
                  title: 'OCR de facturas = registro automatico',
                  desc: 'Cada vez que escaneas una factura o albaran, PLEKO extrae automaticamente: proveedor, productos, cantidades, precios y fecha. Esos datos alimentan tu registro de trazabilidad sin teclear nada.',
                  icon: Scan,
                },
                {
                  title: 'Historico de compras por proveedor',
                  desc: 'Puedes consultar todo lo que has comprado a cada proveedor, con fechas, cantidades y precios. Es exactamente la informacion que el inspector te pedira sobre trazabilidad hacia atras.',
                  icon: FileSearch,
                },
                {
                  title: 'Vinculacion ingrediente-receta',
                  desc: 'Al tener cada receta vinculada a sus ingredientes, y cada ingrediente a su proveedor, puedes rastrear la cadena completa: del plato al proveedor en pocos clics.',
                  icon: Route,
                },
                {
                  title: 'Datos siempre accesibles y buscables',
                  desc: 'Todo queda almacenado en la nube. Puedes buscar por proveedor, por producto, por fecha o por receta en segundos — sin rebuscar en archivadores.',
                  icon: Monitor,
                },
              ].map(({ title, desc, icon: Icon }) => (
                <div key={title} className="flex gap-4 p-5 bg-teal-50 rounded-xl border border-teal-200">
                  <Icon className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-800 mb-1">{title}</p>
                    <p className="text-slate-600 text-sm">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* ═══════════ SECTION 12: ERRORES COMUNES ═══════════ */}
            <h2 id="errores" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Errores comunes en trazabilidad alimentaria
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Estos son los errores que los inspectores de Sanidad encuentran con mas frecuencia en restaurantes. Evitalos para pasar las inspecciones sin problemas:
            </p>
            <div className="space-y-3 mb-10">
              {[
                {
                  error: 'No registrar el numero de lote',
                  desc: 'Sin el lote, no puedes vincular un producto con una alerta alimentaria. Es el dato mas critico y el que mas se omite.',
                },
                {
                  error: 'Tirar los albaranes y facturas',
                  desc: 'Son tu prueba documental. Debes conservarlos al menos 5 anos. Con un sistema digital, el escaneo los preserva indefinidamente.',
                },
                {
                  error: 'No etiquetar los productos almacenados',
                  desc: 'Producto sin etiqueta = producto sin trazabilidad. Todo lo que entra en camara o almacen debe identificarse con nombre, lote y fecha.',
                },
                {
                  error: 'Mezclar lotes del mismo producto',
                  desc: 'Al rellenar un recipiente con producto nuevo sin vaciar el anterior, pierdes la trazabilidad del lote. Vacia primero, luego rellena.',
                },
                {
                  error: 'No actualizar los registros al cambiar de proveedor',
                  desc: 'Un cambio de proveedor puede implicar cambios en alergenos, origen del producto o condiciones de conservacion. Registralo siempre.',
                },
                {
                  error: 'Registrar solo los datos del proveedor principal',
                  desc: 'Si compras puntualmente en un mercado o a un proveedor secundario, esas compras tambien deben quedar registradas con los mismos datos.',
                },
              ].map(({ error, desc }) => (
                <div key={error} className="flex gap-3 p-4 bg-red-50 rounded-xl border border-red-100">
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{error}</p>
                    <p className="text-slate-600 text-xs mt-1">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ═══════════ FAQ ═══════════ */}
          <div id="faqs" className="mt-16 scroll-mt-20">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
              Preguntas frecuentes sobre trazabilidad alimentaria
            </h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4 flex-shrink-0">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* ═══════════ INTERNAL LINKS ═══════════ */}
          <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-teal-600" /> Guias relacionadas
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/alergenos-restaurante', label: 'Alergenos en restaurantes: normativa y como cumplirla' },
                { to: '/desperdicio-alimentario-restaurante', label: 'Desperdicio alimentario: normativa y prevencion' },
                { to: '/ocr-facturas-restaurante', label: 'OCR de facturas para restaurantes' },
                { to: '/escandallo', label: 'Que es un escandallo: guia completa' },
                { to: '/software-gestion-restaurante', label: 'Software de gestion para restaurantes' },
                { to: '/control-costes-restaurante', label: 'Control de costes en restaurantes' },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-800 hover:underline">
                  <ArrowRight className="w-3 h-3 flex-shrink-0" /> {label}
                </Link>
              ))}
            </div>
          </div>

          {/* ═══════════ FINAL CTA ═══════════ */}
          <div className="mt-16 bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-8 text-center text-white">
            <Route className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">
              Digitaliza la trazabilidad de tu restaurante con PLEKO
            </h2>
            <p className="text-teal-100 mb-6 max-w-xl mx-auto">
              Escanea facturas con OCR, registra proveedores automaticamente, vincula ingredientes con recetas y
              ten toda la informacion lista para cualquier inspeccion. 14 dias gratis.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-teal-700 font-bold rounded-xl hover:bg-teal-50 transition-colors"
            >
              Empezar gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-teal-200 text-sm">Sin tarjeta de credito. Configurado en 2 minutos.</p>
          </div>
        </section>
      </div>
    </>
  );
}
